import { useLazyQuery } from '@apollo/client/react';
import { downloadBase64Pdf } from '@src/components/sco/Advisors/utils/downloadPdf';
import { cleanErrorMessage } from '@src/components/sco/Advisors/utils/sessionFormat';
import { ADVISOR_AGREEMENT_PDF, ADVISOR_COMP_OVERVIEW_PDF } from '@src/gql/queries/AdvisorAgreementPdf';
import { useCallback, useState } from 'react';
type PdfPayload = {
  fileName?: string | null;
  mimeType?: string | null;
  contentBase64?: string | null;
};
const savePdf = (pdf?: PdfPayload | null) => {
  if (!pdf?.contentBase64) {
    throw new Error('PDF was not returned.');
  }
  downloadBase64Pdf(pdf.fileName || 'agreement.pdf', pdf.contentBase64, pdf.mimeType || 'application/pdf');
};
export const useAdvisorAgreementPdfDownload = () => {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetchAgreementPdf] = useLazyQuery<{
    advisorAgreementPdf?: PdfPayload | null;
  }>(ADVISOR_AGREEMENT_PDF);
  const [fetchOverviewPdf] = useLazyQuery<{
    advisorCompOverviewPdf?: PdfPayload | null;
  }>(ADVISOR_COMP_OVERVIEW_PDF);
  const downloadAgreementPdf = useCallback(async (advisorAgreementId: string, sourceKey?: 'generated' | 'signed' | 'auto') => {
    setError(null);
    setBusyId(advisorAgreementId);
    try {
      const result = await fetchAgreementPdf({
        variables: {
          advisorAgreementId,
          sourceKey: sourceKey && sourceKey !== 'auto' ? sourceKey : null
        },
        fetchPolicy: 'network-only'
      });
      if (result.error) {
        throw result.error;
      }
      savePdf(result.data?.advisorAgreementPdf);
    } catch (err) {
      setError(cleanErrorMessage(err, 'Could not download this agreement PDF.'));
    } finally {
      setBusyId(null);
    }
  }, [fetchAgreementPdf]);
  const downloadOverviewPdf = useCallback(async (input: {
    companyStageKey: string;
    contributionTierKey: string;
    caliberKey: string;
    warmIntroKey: string;
    hoursPerMonth: number;
    valuationInDollars: number;
  }) => {
    setError(null);
    setBusyId('overview');
    try {
      const result_0 = await fetchOverviewPdf({
        variables: input,
        fetchPolicy: 'network-only'
      });
      if (result_0.error) {
        throw result_0.error;
      }
      savePdf(result_0.data?.advisorCompOverviewPdf);
    } catch (err_0) {
      setError(cleanErrorMessage(err_0, 'Could not download the compensation overview PDF.'));
    } finally {
      setBusyId(null);
    }
  }, [fetchOverviewPdf]);
  return {
    busyId,
    error,
    downloadAgreementPdf,
    downloadOverviewPdf
  };
};